import { useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useJacintaMemory } from '@/modules/brain/store/memoryStore';
import { MemoryCard } from '@/modules/brain/components/MemoryCard';
import {
  MemoryFilterBar,
  type CategoryFilter,
  type SourceFilter,
} from '@/modules/brain/components/MemoryFilterBar';
import { MemoryStats } from '@/modules/brain/components/MemoryStats';
import type { JacintaMemoryEntry } from '@/modules/shared/types/yc-demo';

function buildCounts(memories: JacintaMemoryEntry[]) {
  const categoryCounts: Record<CategoryFilter, number> = {
    all: memories.length,
    scoring: 0,
    sourcing: 0,
    pipeline: 0,
    culture: 0,
    compensation: 0,
  };
  const sourceCounts: Record<SourceFilter, number> = {
    all: memories.length,
    score_edit: 0,
    pipeline_outcome: 0,
    observed: 0,
    manual: 0,
  };
  for (const m of memories) {
    categoryCounts[m.category] = (categoryCounts[m.category] ?? 0) + 1;
    sourceCounts[m.source] = (sourceCounts[m.source] ?? 0) + 1;
  }
  return { categoryCounts, sourceCounts };
}

export function JacintaMemoryPage() {
  const memories = useJacintaMemory((s) => s.memories);

  const [category, setCategory] = useState<CategoryFilter>('all');
  const [source, setSource] = useState<SourceFilter>('all');
  const [query, setQuery] = useState('');

  // Track which entries should animate in. Anything created since the page
  // first mounted is "fresh" — that's how the score-edit toast lands.
  const mountedAtRef = useRef<number>(Date.now());

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return memories.filter((m) => {
      if (category !== 'all' && m.category !== category) return false;
      if (source !== 'all' && m.source !== source) return false;
      if (!q) return true;
      const haystack = [m.insight, m.trigger, m.attributedTo.name].join('  ').toLowerCase();
      return haystack.includes(q);
    });
  }, [memories, category, source, query]);

  const { categoryCounts, sourceCounts } = useMemo(() => buildCounts(memories), [memories]);
  const filteredStats = useMemo(() => filtered, [filtered]);

  const allFilteredEqualsAll = filtered.length === memories.length;

  return (
    <div className="memory-page">
      <header className="memory-hero">
        <div className="memory-hero-portrait">
          <img src="/avatars/jacinta.svg" alt="Jacinta" draggable={false} />
        </div>
        <div className="memory-hero-text">
          <p className="memory-hero-eyebrow">Brain · Memory</p>
          <h1 className="memory-hero-title">Things Lantern has learned about your team</h1>
          <MemoryStats memories={allFilteredEqualsAll ? memories : filteredStats} />
        </div>
      </header>

      <MemoryFilterBar
        category={category}
        onCategoryChange={setCategory}
        source={source}
        onSourceChange={setSource}
        query={query}
        onQueryChange={setQuery}
        categoryCounts={categoryCounts}
        sourceCounts={sourceCounts}
      />

      <div className="memory-list">
        {filtered.length === 0 ? (
          <div className="memory-empty">
            <p>No memories match. Lantern is still learning.</p>
            <button
              type="button"
              className="memory-empty-action"
              onClick={() => {
                setCategory('all');
                setSource('all');
                setQuery('');
              }}
            >
              Clear filters
            </button>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {filtered.map((entry) => {
              const createdAt = new Date(entry.createdAt).getTime();
              const isFresh = createdAt >= mountedAtRef.current;
              return <MemoryCard key={entry.id} entry={entry} isFresh={isFresh} />;
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}

